import React from "react";
import { motion } from "framer-motion";
import { Route, ClipboardCheck } from "lucide-react";
import { COMMERCIAL_IMAGES } from "@/data/images";

const dispatchBullets = [
  "Live driver-to-dispatch communication",
  "Real-time ETA & load status monitoring",
  "Proactive re-routing around delays or weather",
  "One dedicated point of contact per account",
];

const commercialFeatures = [
  "Scheduled route deliveries — daily, weekly, or on-demand",
  "White-glove handling for sensitive freight",
  "Signed proof-of-delivery on every drop",
  "Retail, distribution, warehouse, and construction pick-ups",
  "Multi-stop routing with real-time visibility",
  "Dedicated account manager for contract customers",
];

export function DispatchShowcase() {
  return (
    <>
      {/* DISPATCH / OPERATIONS SHOWCASE */}
      <section className="py-20 lg:py-28 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 relative"
          >
            <img
              src={COMMERCIAL_IMAGES.dispatch}
              alt="Laronta Pro dispatch operations team briefing"
              className="w-full h-auto object-cover border border-slate-200"
              data-testid="commercial-dispatch-image"
            />
            <div className="absolute -bottom-5 -right-5 hidden md:block bg-primary-brand text-white px-5 py-4">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold">Operations</div>
              <div className="font-heading font-black text-lg mt-1">Dispatch that never sleeps.</div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">The People Behind the Wheel</div>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-slate-900">
              A dispatch team that treats your freight like their own.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Our commercial operation runs a live dispatch desk that monitors weather, traffic,
              and hours-of-service in real time. Every driver checks in at pickup, in-transit,
              and at delivery. If anything changes, you hear from a human — not an auto-reply.
            </p>
            <ul className="mt-6 space-y-3">
              {dispatchBullets.map((b) => (
                <li key={b} className="flex items-center gap-3 text-slate-800">
                  <Route className="h-4 w-4 text-primary-brand flex-none" />
                  <span className="text-sm">{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE HANDLE */}
      <section className="py-20 lg:py-28 border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">What We Handle</div>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-slate-900">
              Everything a growing business needs to move.
            </h2>
            <p className="mt-4 text-slate-600">
              Pallets, appliances, retail restocks, construction supplies, event freight, and one-off oversized items.
              If it moves by truck and needs to be there on time, we're the crew for it.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {commercialFeatures.map((f, i) => (
              <div key={f} data-testid={`commercial-feature-${i}`} className="bg-white p-6 lg:p-7 flex items-start gap-3">
                <ClipboardCheck className="h-5 w-5 text-primary-brand flex-none mt-0.5" />
                <span className="text-slate-800 text-sm leading-relaxed">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
